// src/components/personal/sections/OrdersSection/hooks/useOrderSorting.ts
import { useState, useCallback, useMemo } from 'react';
import { Order } from '../types/types';
import { sortOrders } from '../utils/orderFilters';

type SortDirection = 'asc' | 'desc';

interface UseOrderSortingProps {
  orders: Order[];
  initialSortColumn?: string;
  initialSortDirection?: SortDirection;
  onSortChange?: (column: string, direction: SortDirection) => void;
}

interface UseOrderSortingReturn {
  // Состояние сортировки
  sortColumn: string;
  sortDirection: SortDirection;
  sortedOrders: Order[];
  
  // Методы управления сортировкой
  setSorting: (column: string, direction: SortDirection) => void;
  toggleSortDirection: () => void;
  sortBy: (column: string) => void;
}

export const useOrderSorting = ({
  orders,
  initialSortColumn = 'created_at',
  initialSortDirection = 'desc',
  onSortChange
}: UseOrderSortingProps): UseOrderSortingReturn => {
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Состояние сортировки
  const [sortColumn, setSortColumn] = useState<string>(initialSortColumn);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSortDirection);
  
  // Метод установки параметров сортировки
  const setSorting = useCallback((column: string, direction: SortDirection) => {
    setSortColumn(column);
    setSortDirection(direction);
    
    if (onSortChange) {
      onSortChange(column, direction);
    }
  }, [onSortChange]);
  
  // Метод переключения направления сортировки
  const toggleSortDirection = useCallback(() => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    setSortDirection(newDirection);
    
    if (onSortChange) {
      onSortChange(sortColumn, newDirection);
    }
  }, [sortColumn, sortDirection, onSortChange]);
  
  // Метод сортировки по колонке
  const sortBy = useCallback((column: string) => {
    // Если колонка та же, меняем направление
    if (column === sortColumn) {
      toggleSortDirection();
    } else {
      // Иначе устанавливаем новую колонку с направлением по умолчанию (asc)
      setSorting(column, 'asc');
    }
  }, [sortColumn, toggleSortDirection, setSorting]);
  
  // Отсортированный список заказов
  const sortedOrders = useMemo(() => {
    if (orders.length === 0) {
      return [];
    }
    
    if (DEBUG) console.log(`Сортировка ${orders.length} заказов по ${sortColumn} (${sortDirection})`);
    
    return sortOrders(orders, sortColumn, sortDirection);
  }, [orders, sortColumn, sortDirection, DEBUG]);
  
  return {
    sortColumn,
    sortDirection,
    sortedOrders,
    setSorting,
    toggleSortDirection,
    sortBy
  };
};